import { useMemo, useState } from "react";
import { ApiError } from "../../lib/api";
import { PreparationHeader } from "./PreparationHeader";
import { StaffEditorDrawer } from "./StaffEditorDrawer";
import { TeamImportToolbar } from "./TeamImportToolbar";
import { useTournamentPreparation } from "./useTournamentPreparation";
import "./preparation.css";
import "./preparation-edit.css";

const ROUNDS = ["R1", "R2", "R3", "R4"] as const;

function roleLabel(value: string) {
  if (value === "scorer") return "SC";
  if (value === "setup") return "SET";
  if (value === "dm") return "DM";
  if (value === "off") return "—";
  return value.slice(0, 3).toUpperCase();
}

export function TeamPage() {
  const state = useTournamentPreparation();
  const data = state.preparation.data;
  const [query, setQuery] = useState("");
  const [baseFilter, setBaseFilter] = useState("");
  const [onlyDrivers, setOnlyDrivers] = useState(false);
  const [onlyTvExcluded, setOnlyTvExcluded] = useState(false);
  const [selectedPersonId, setSelectedPersonId] = useState("");

  const rows = useMemo(() => {
    const q = query.trim().toLocaleLowerCase("pl");
    return (data?.staff || []).filter((person) => {
      if (q && !`${person.displayName} ${person.baseName} ${person.roomName}`.toLocaleLowerCase("pl").includes(q)) return false;
      if (baseFilter && person.baseId !== baseFilter) return false;
      if (onlyDrivers && !person.driverEligible) return false;
      if (onlyTvExcluded && person.tvEligible) return false;
      return true;
    });
  }, [data?.staff, query, baseFilter, onlyDrivers, onlyTvExcluded]);

  const selectedPerson = data?.staff.find((person) => person.personId === selectedPersonId) || null;
  const error = state.preparation.error || state.tournaments.error;
  const errorMessage = error instanceof ApiError ? error.message : error instanceof Error ? error.message : "";

  return (
    <section className="page-grid prep-page team-page">
      <PreparationHeader
        eyebrow="TOURNAMENT TEAM"
        title={state.selectedTournament?.name || "Obsada turnieju"}
        description="Obsada konkretnego turnieju: role R1–R4, nocleg i pokój, kierowcy, TV oraz wyjątki. Importy są w tym samym miejscu."
        season={state.season}
        tournamentId={state.tournamentId}
        tournaments={state.tournaments.data?.tournaments || []}
        onSeasonChange={state.setSeason}
        onTournamentChange={(value) => { setSelectedPersonId(""); state.setTournamentId(value); }}
      />

      {errorMessage ? <article className="panel prep-error"><h3>Nie udało się wczytać obsady</h3><p>{errorMessage}</p></article> : null}
      {state.preparation.isPending && state.tournamentId ? <article className="panel round-loading"><div className="round-loading-bar"/><span>Wczytuję obsadę…</span></article> : null}

      {data ? (
        <>
          <TeamImportToolbar tournamentId={state.tournamentId} data={data} />

          <div className="prep-kpi-grid team-kpis">
            <article className="panel prep-kpi"><span>Cała obsada</span><strong>{data.summary.staffCount}</strong><small>{data.summary.activeEmployeePoolCount} aktywnych w bazie pracowników</small></article>
            <article className="panel prep-kpi"><span>Kierowcy</span><strong>{data.summary.driverCount}</strong><small>na tym turnieju</small></article>
            <article className="panel prep-kpi"><span>Przypisany pokój</span><strong>{data.staff.filter((person) => Boolean(person.roomName)).length}</strong><small>solver może pilnować współlokatorów</small></article>
            <article className="panel prep-kpi"><span>Wykluczeni TV</span><strong>{data.summary.tvExcludedCount}</strong><small>pozostali domyślnie mogą TV</small></article>
          </div>

          <article className="panel team-filter-bar">
            <label className="team-search"><span>Szukaj</span><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Nazwisko, nocleg lub pokój" /></label>
            <label><span>Nocleg</span><select value={baseFilter} onChange={(event) => setBaseFilter(event.target.value)}><option value="">Wszystkie</option>{data.accommodations.map((base) => <option key={base.id} value={base.id}>{base.name}</option>)}</select></label>
            <button type="button" className={onlyDrivers ? "filter-toggle active" : "filter-toggle"} onClick={() => setOnlyDrivers((value) => !value)}>Kierowcy</button>
            <button type="button" className={onlyTvExcluded ? "filter-toggle active danger" : "filter-toggle"} onClick={() => setOnlyTvExcluded((value) => !value)}>Nie do TV</button>
            <span className="team-filter-count">{rows.length} / {data.staff.length}</span>
          </article>

          <article className="panel team-matrix-card">
            <header className="prep-card-head"><div><span className="eyebrow">ROLE MATRIX</span><h3>Obsada R1–R4</h3></div><p className="team-edit-hint">Kliknij osobę → role, nocleg, pokój, K, TV i wyjątki</p></header>
            <div className="team-table-wrap">
              <table className="team-table">
                <thead><tr><th>Osoba</th><th>Nocleg</th><th>Pokój</th><th>K</th><th>TV</th>{ROUNDS.map((round) => <th key={round}>{round}</th>)}<th>Aktywne</th></tr></thead>
                <tbody>{rows.map((person) => (
                  <tr key={person.personId} className={`editable ${selectedPersonId === person.personId ? "selected" : ""}`} onClick={() => setSelectedPersonId(person.personId)}>
                    <td><strong>{person.displayName}</strong>{person.employeeId ? <small>{person.employeeId}</small> : null}</td>
                    <td><span className="team-base-pill">{person.baseName}</span></td>
                    <td>{person.roomName ? <span className="team-base-pill room">{person.roomName}</span> : <span className="team-muted">—</span>}</td>
                    <td>{person.driverEligible ? <span className="team-flag driver">K</span> : <span className="team-muted">—</span>}</td>
                    <td>{person.tvEligible ? <span className="team-flag tv">TAK</span> : <span className="team-flag no-tv">NIE</span>}</td>
                    {ROUNDS.map((round) => <td key={round}><span className={`team-role ${person.roles[round] || "off"}`}>{roleLabel(person.roles[round] || "off")}</span></td>)}
                    <td><strong>{person.activeRounds.length}</strong>{Object.keys(person.roundConstraints || {}).length ? <small className="team-constraint-mark">WYJĄTKI</small> : null}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
            {!rows.length ? <div className="prep-empty">Brak osób pasujących do filtrów.</div> : null}
          </article>

          <div className="team-base-grid">
            {data.accommodations.filter((base) => base.active).map((base) => {
              const members = data.staff.filter((person) => person.baseId === base.id);
              const drivers = members.filter((person) => person.driverEligible).length;
              const rooms = new Set(members.map((person) => person.roomName).filter(Boolean)).size;
              return (
                <article className="panel team-base-card" key={base.id}>
                  <header><div><span className="eyebrow">{base.type}</span><h3>{base.name}</h3></div><strong>{members.length}</strong></header>
                  <div><span>{drivers} kierowców</span><span>{rooms} pokoi</span><span>{members.filter((person) => !person.tvEligible).length} bez TV</span></div>
                  <p>{members.slice(0, 8).map((person) => person.displayName).join(" · ")}{members.length > 8 ? ` · +${members.length - 8}` : ""}</p>
                </article>
              );
            })}
          </div>

          {selectedPerson ? <StaffEditorDrawer tournamentId={state.tournamentId} data={data} person={selectedPerson} onClose={() => setSelectedPersonId("")} /> : null}
        </>
      ) : null}
    </section>
  );
}
